import { GraphQLExecutor } from '@app/model/_common/service/GraphQLExecutor';
import {
	CREATE_COMPANY_HOTEL_PRICING_ARCHITECTURE_DERIVATIVE_MUTATION,
	CREATE_COMPANY_HOTEL_PRICING_ARCHITECTURE_MUTATION,
	CREATE_PRICE_RECOMMENDATION_FEEDBACK_MUTATION,
	EDIT_COMPANY_HOTEL_PRICING_ARCHITECTURE_DERIVATIVE_MUTATION,
	FIND_COMPANY_HOTEL_BOOKING_PACE_QUERY,
	FIND_COMPANY_HOTEL_BOOKING_PACE_FOR_PERIOD_QUERY,
	FIND_COMPANY_HOTEL_NOT_ASSIGNED_PRICING_ROOMS_QUERY, FIND_COMPANY_HOTEL_PRICING_ARCHITECTURE_QUERY,
	FIND_COMPANY_HOTEL_PRICING_ARCHITECTURES_QUERY,
	FIND_COMPANY_HOTEL_PRICING_ROOMS_QUERY, FIND_COMPANY_HOTEL_PRICING_ROOMS_WITH_PRICE_QUERY,
	FIND_COMPANY_HOTEL_PRICING_STRATEGY_COMPETITORS_IMPORTANCE_QUERY,
	FIND_COMPANY_HOTEL_PRICING_SUBMISSION_QUERY,
	FIND_COMPANY_HOTEL_PRICING_SUBMISSIONS_FOR_STAT_DATE_QUERY, PAGE_COMPANY_HOTEL_PRICING_SUBMISSIONS_QUERY,
	FIND_COMPANY_HOTEL_PRICE_CHANGES_FOR_STAT_DATE_QUERY,
	REMOVE_PRICING_ARCHITECTURE_DERIVATIVE_MUTATION,
	REMOVE_PRICING_ARCHITECTURE_MUTATION,
	SAVE_COMPANY_HOTEL_PRICING_STRATEGY_COMPETITORS_IMPORTANCE_MUTATION,
	SAVE_PRICING_STRATEGY_MUTATION,
	SUBMIT_MANUAL_PRICING_MUTATION,
	SUBMIT_BULK_MANUAL_PRICING_MUTATION,
	SUBMIT_MONTHLY_PRICING_MUTATION,
	SUBMIT_PRICING_MUTATION, SUBMIT_SELECTIVE_PRICING_MUTATION, SWITCH_PRICING_ARCHITECTURE_ORDER_INDEX_MUTATION,
} from '@app/model/pricing-automation/service/PricingAutomationGraphQLQueries';
import PricingRoom from '@app/model/pricing-automation/PricingRoom';
import PricingArchitecture from '@app/model/pricing-automation/PricingArchitecture';
import HotelRoomMappingType from '@app/model/market_state/HotelRoomMappingType';
import CreatePricingArchitectureDerivativeInput from '../input/CreatePricingArchitectureDerivativeInput';
import PricingStrategyInput from '@app/model/pricing-automation/input/PricingStrategyInput';
import Return from '@app/model/_common/Return';
import PricingIntegrationInput from '@app/model/pricing-automation/input/PricingIntegrationInput';
import PricingStrategyCompetitorImportance from '@app/model/pricing-automation/PricingStrategyCompetitorImportance';
import PricingStrategyCompetitorsImportanceInput
	from '@app/model/pricing-automation/input/PricingStrategyCompetitorsImportanceInput';
import EditPricingArchitectureDerivativeInput
	from '@app/model/pricing-automation/input/EditPricingArchitectureDerivativeInput';
import PriceRecommendationFeedbackInput from '@app/model/pricing-automation/input/PriceRecommendationFeedbackInput';
import CompanyHotelBookingPace from '../output/CompanyHotelBookingPace';
import CompanyHotelBookingPaceForPeriodItem from '../output/CompanyHotelBookingPaceForPeriodItem';
import Month from '@app/model/market_state/Month';
import SubmitPricingResultOutput from '../output/SubmitPricingResultOutput';
import PageRequest from '@app/model/_common/paging/PageRequest';
import PricingSubmission from '@app/model/pricing-automation/PricingSubmission';
import { PriceChangeSubmission } from '@app/model/pricing-automation/PriceChangeSubmission';
import PageResult from '@app/model/_common/paging/PageResult';
import PricingRoomWithPriceOutput from '@app/model/pricing-automation/output/PricingRoomWithPriceOutput';
import ManualPricingIntegrationInput from '@app/model/pricing-automation/input/ManualPricingIntegrationInput';
import SubmitSelectivePricingInput from '@app/model/pricing-automation/input/SubmitSelectivePricingInput';
import BulkManualPricingInput from '@app/model/pricing-automation/input/BulkManualPricingInput';
import SubmitBulkManualPricingResultOutput from '@app/model/pricing-automation/output/SubmitBulkManualPricingResultOutput';


class PricingAutomationService {

	private static toPricingArchitecture(data: PricingArchitecture): PricingArchitecture {
		return new PricingArchitecture(data);
	}

	private static toPricingArchitectures(data: PricingArchitecture[]): PricingArchitecture[] {
		return data.map(PricingAutomationService.toPricingArchitecture);
	}

	private static toPricingSubmission(data: PricingSubmission): PricingSubmission {
		return new PricingSubmission(data);
	}

	private static toPricingSubmissionsPage(data: PageResult<PricingSubmission>): PageResult<PricingSubmission> {
		return {
			...data,
			content: data.content.map(PricingAutomationService.toPricingSubmission),
		};
	}

	private static toPricingSubmissions(data: PricingSubmission[]): PricingSubmission[] {
		return data.map(PricingAutomationService.toPricingSubmission);
	}

	public static findCompanyHotelNotAssignedPricingRooms = (companyHotelId: number) => {
		return GraphQLExecutor.execute<PricingRoom[]>(FIND_COMPANY_HOTEL_NOT_ASSIGNED_PRICING_ROOMS_QUERY, { companyHotelId });
	};

	public static findCompanyHotelPricingRooms = (companyHotelId: number) => {
		return GraphQLExecutor.execute<PricingRoom[]>(FIND_COMPANY_HOTEL_PRICING_ROOMS_QUERY, { companyHotelId });
	};

	public static findCompanyHotelPricingArchitectures = (companyHotelId: number) => {
		return GraphQLExecutor.execute<PricingArchitecture[]>(FIND_COMPANY_HOTEL_PRICING_ARCHITECTURES_QUERY, { companyHotelId })
			.then(PricingAutomationService.toPricingArchitectures);
	};

	public static findCompanyHotelPricingArchitecture = (companyHotelId: number, architectureId: number) => {
		return GraphQLExecutor.execute<PricingArchitecture>(FIND_COMPANY_HOTEL_PRICING_ARCHITECTURE_QUERY, { companyHotelId, architectureId })
			.then(PricingAutomationService.toPricingArchitecture);
	};

	public static createPricingArchitecture = async (input: {
		companyHotelId: number,
		pricingRoomId: number,
		competitorsMappingType: HotelRoomMappingType
	}) => {
		const data = await GraphQLExecutor.execute<PricingArchitecture>(CREATE_COMPANY_HOTEL_PRICING_ARCHITECTURE_MUTATION, input);
		return PricingAutomationService.toPricingArchitecture(data);
	};

	public static createPricingArchitectureDerivative = (input: CreatePricingArchitectureDerivativeInput) => {
		return GraphQLExecutor.execute<PricingArchitecture>(CREATE_COMPANY_HOTEL_PRICING_ARCHITECTURE_DERIVATIVE_MUTATION, { input })
			.then(PricingAutomationService.toPricingArchitecture);
	};

	public static editPricingArchitectureDerivative = (input: EditPricingArchitectureDerivativeInput) => {
		return GraphQLExecutor.execute<PricingArchitecture>(EDIT_COMPANY_HOTEL_PRICING_ARCHITECTURE_DERIVATIVE_MUTATION, { input })
			.then(PricingAutomationService.toPricingArchitecture);
	};

	public static removePricingArchitecture = (input: { companyHotelId: number, architectureId: number }) => {
		return GraphQLExecutor.execute<PricingArchitecture[]>(REMOVE_PRICING_ARCHITECTURE_MUTATION, input)
			.then(PricingAutomationService.toPricingArchitectures);
	};

	public static removePricingArchitectureDerivative = async (input: {
		companyHotelId: number,
		architectureId: number,
		derivativeId: number
	}) => {
		const data = await GraphQLExecutor.execute<PricingArchitecture[]>(REMOVE_PRICING_ARCHITECTURE_DERIVATIVE_MUTATION, input);
		return PricingAutomationService.toPricingArchitectures(data);
	};



	public static savePricingStrategy = (input: PricingStrategyInput) => {
		return GraphQLExecutor.execute<PricingArchitecture>(SAVE_PRICING_STRATEGY_MUTATION, { input })
			.then(PricingAutomationService.toPricingArchitecture);
	};

	public static submitPricing(variables: { companyHotelId: number, input: PricingIntegrationInput }) {
		return GraphQLExecutor.execute<SubmitPricingResultOutput>(SUBMIT_PRICING_MUTATION, variables);
	}

	public static submitManualPricing(variables: { companyHotelId: number, input: ManualPricingIntegrationInput }) {
		return GraphQLExecutor.execute<Return>(SUBMIT_MANUAL_PRICING_MUTATION, variables);
	}

	public static submitMonthlyPricing(variables: { companyHotelId: number, month: Month, year: number }) {
		return GraphQLExecutor.execute<SubmitPricingResultOutput>(SUBMIT_MONTHLY_PRICING_MUTATION, variables);
	}

	public static submitSelectivePricing(variables: { companyHotelId: number, input: SubmitSelectivePricingInput }) {
		return GraphQLExecutor.execute<SubmitPricingResultOutput>(SUBMIT_SELECTIVE_PRICING_MUTATION, variables);
	}

	public static submitBulkManualPricing(variables: { companyHotelId: number, input: BulkManualPricingInput }) {
		return GraphQLExecutor.execute<SubmitBulkManualPricingResultOutput>(SUBMIT_BULK_MANUAL_PRICING_MUTATION, variables);
	}

	public static findCompanyHotelPricingStrategyCompetitorImportances(companyHotelId: number) {
		return GraphQLExecutor.execute<PricingStrategyCompetitorImportance[]>(FIND_COMPANY_HOTEL_PRICING_STRATEGY_COMPETITORS_IMPORTANCE_QUERY, { companyHotelId });
	}

	public static savePricingStrategyCompetitorImportance(variables: {
		input: PricingStrategyCompetitorsImportanceInput
	}) {
		return GraphQLExecutor.execute<PricingStrategyCompetitorImportance>(SAVE_COMPANY_HOTEL_PRICING_STRATEGY_COMPETITORS_IMPORTANCE_MUTATION, variables);
	}

	public static createPricingRecommendationFeedback(variables: {
		input: PriceRecommendationFeedbackInput
	}) {
		return GraphQLExecutor.execute<PricingStrategyCompetitorImportance>(CREATE_PRICE_RECOMMENDATION_FEEDBACK_MUTATION, variables);
	}

	public static switchPricingArchitectureOrderIndex(variables: {
		companyHotelId: number,
		architectureId: number,
		orderIndex: number,
	}) {
		return GraphQLExecutor.execute<PricingArchitecture[]>(SWITCH_PRICING_ARCHITECTURE_ORDER_INDEX_MUTATION, variables);
	}

	static findCompanyHotelBookingPace(companyHotelId: number, marketStatePromptId: number) {
		return GraphQLExecutor.execute<CompanyHotelBookingPace>(FIND_COMPANY_HOTEL_BOOKING_PACE_QUERY, {
			companyHotelId,
			marketStatePromptId,
		});
	}

	static findCompanyHotelBookingPaceForPeriod(companyHotelId: number, year: number, month: Month) {
		return GraphQLExecutor.execute<CompanyHotelBookingPaceForPeriodItem[]>(FIND_COMPANY_HOTEL_BOOKING_PACE_FOR_PERIOD_QUERY, {
			companyHotelId,
			year,
			month,
		});
	}

	static pageCompanyHotelPricingSubmissions(companyHotelId: number, pageRequest: PageRequest) {
		return GraphQLExecutor.execute<PageResult<PricingSubmission>>(PAGE_COMPANY_HOTEL_PRICING_SUBMISSIONS_QUERY, {
			companyHotelId,
			pageRequest,
		}).then(PricingAutomationService.toPricingSubmissionsPage);
	}

	static async findCompanyHotelPricingSubmission(companyHotelId: number, submissionId: number) {
		const data = await GraphQLExecutor.execute<PricingSubmission>(FIND_COMPANY_HOTEL_PRICING_SUBMISSION_QUERY, {
			companyHotelId,
			submissionId,
		});
		return !!data? PricingAutomationService.toPricingSubmission(data): null;
	}

	static findCompanyHotelPricingSubmissionsForStatDate(companyHotelId: number, statDate: string) {
		return GraphQLExecutor.execute<PricingSubmission[]>(FIND_COMPANY_HOTEL_PRICING_SUBMISSIONS_FOR_STAT_DATE_QUERY, {
			companyHotelId,
			statDate,
		}).then(PricingAutomationService.toPricingSubmissions);
	}

	static findCompanyHotelPriceChangesForStatDate(companyHotelId: number, statDate: string, fromDate: string) {
		return GraphQLExecutor.execute<PriceChangeSubmission[]>(FIND_COMPANY_HOTEL_PRICE_CHANGES_FOR_STAT_DATE_QUERY, {
			companyHotelId, statDate, fromDate,
		});
	}

	static findCompanyHotelPricingRoomWithPrice(companyHotelId: number, statDate: string) {
		return GraphQLExecutor.execute<PricingRoomWithPriceOutput[]>(FIND_COMPANY_HOTEL_PRICING_ROOMS_WITH_PRICE_QUERY, {
			companyHotelId,
			statDate,
		});
	}
}

export default PricingAutomationService;
